import { Dispatch, FC, SetStateAction, useContext } from 'react'
import { Collapse, List, ListItem } from '@material-ui/core'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { ActivityFilterItemById } from 'generated/graphql'
import { FilterByKeys, RecruitListScreenContext } from '../../context'
import Checkbox from './Checkbox'
import RecruitFilterDetailPanel from './RecruitFilterDetailPanel'

interface RecruitFilterItemProps<T = ActivityFilterItemById> {
  width?: number
  filterTitle: string
  filterItems: Array<T>
  filterKey: FilterByKeys
  isHidden?: boolean
  isDetailPanelOpen?: boolean
  setOpenDetailPanelKey: Dispatch<SetStateAction<FilterByKeys | null>>
  DetailPanel?: FC<{
    className?: string
    defaultCategoryIds?: string[]
  }>
  onClickForDetailPanel?: (item: T) => void
  onTitleClick: () => void
}

type TStyledProps = {
  maxWidth?: number
}

const RecruitFilterItem: FC<RecruitFilterItemProps> = (props) => {
  const {
    width,
    filterTitle,
    filterItems,
    filterKey,
    setOpenDetailPanelKey,
    isDetailPanelOpen = false,
    isHidden = true,
    DetailPanel,
    onClickForDetailPanel,
    onTitleClick,
  } = props

  const listDataTestID = `new-activity-list-filter-item-${filterKey}`
  const { state, dispatch } = useContext(RecruitListScreenContext)

  const isAllowedDetailPanel = !!onClickForDetailPanel && DetailPanel

  return (
    <>
      {isAllowedDetailPanel && (
        <RecruitFilterDetailPanel isClose={isDetailPanelOpen}>
          <DetailPanel
            className="detail-panel"
            defaultCategoryIds={state.filterBy.categoryIDs as Array<string>}
          />
        </RecruitFilterDetailPanel>
      )}
      <StyledWrapper
        className={clsx({
          open: isAllowedDetailPanel && isDetailPanelOpen,
        })}
        maxWidth={width || 200}
      >
        <p
          className="filter-title"
          onClick={() => {
            onTitleClick()
            setOpenDetailPanelKey((prev) => {
              if (prev === filterKey) {
                return null
              }
              return filterKey
            })
          }}
        >
          <span>{filterTitle}</span>
          {isAllowedDetailPanel && (
            <img
              src="/static/images/activity/filter-arrow-icon.svg"
              className={clsx('icon-expand-more', {
                expanded: isDetailPanelOpen,
              })}
              alt="expand-more"
            />
          )}
        </p>
        <Collapse in={!isHidden}>
          <div
            className="filter-list"
            key={filterKey}
            style={{ maxWidth: width }}
          >
            <div className="filter-scroll">
              <List data-testid={listDataTestID}>
                {filterItems.map((item: ActivityFilterItemById) => {
                  const key = item.id
                  const itemList: string[] =
                    (state.filterBy[filterKey] as string[]) || []
                  const checked = itemList.includes(key)

                  const handleClick = () => {
                    if (isAllowedDetailPanel) {
                      onClickForDetailPanel && onClickForDetailPanel(item)
                      setOpenDetailPanelKey(filterKey)
                      return
                    }
                    dispatch({
                      type: 'FILTER_BY_TOGGLE_FILTER_ITEM',
                      listItem: item,
                      fieldName: filterKey,
                    })
                  }

                  return (
                    <ListItem
                      className="list-item"
                      key={key}
                      onClick={handleClick}
                      data-testid={`${listDataTestID}-${key}`}
                    >
                      <div className="item-content">
                        <Checkbox
                          checked={checked}
                          className="content-checkbox"
                        />
                        <b className="list-title">{item.name}</b>
                      </div>
                    </ListItem>
                  )
                })}
              </List>
            </div>
          </div>
        </Collapse>
      </StyledWrapper>
    </>
  )
}
export default RecruitFilterItem

const StyledWrapper = styled.div<TStyledProps>`
  ${({ theme, maxWidth }) => css`
    width: 100%;
    max-width: ${maxWidth}px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-size: 15px;
    text-align: center;
    padding: 0;
    border: 1px solid ${theme.line.line_2};
    border-left: 1px solid transparent;

    &:first-child {
      border-left: 1px solid ${theme.line.line_2};
    }

    &.open {
      background-color: #003056;
      color: #ffffff;
    }

    .filter-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      padding: 14px 20px;
      font-size: 14px;
      text-align: left;
      font-weight: 500;
      line-height: 21px;

      .icon-expand-more {
        transform: rotate(0deg);
        transition: transform 0.15s ease-in-out;

        &.expanded {
          transform: rotate(180deg);
          filter: invert(1);
        }
      }
    }

    .filter-list {
      width: 100%;
      height: 254px;
      position: relative;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-top: 1px solid ${theme.line.line_2};
    }

    .filter-scroll {
      position: absolute;
      inset: 0;
      overflow: scroll;
      margin-right: -15px;
      margin-bottom: -15px;
      &::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 1px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: none;
        box-shadow: none;
        border: none;
      }
    }

    .list-item {
      cursor: pointer;
      padding: 0 20px;
      border-bottom: 0;

      &:hover {
        background-color: ${theme.background.bg_3};
      }

      .list-title {
        display: block;
        width: 100%;
        color: ${theme.color.text_2};
        font-size: 12px;
        font-weight: 400;
        height: 21px;
        line-height: 21px;
      }

      .item-content {
        display: flex;
        height: 42px;
        flex: 1;
        align-items: center;
        border-bottom: 1px solid #eeeeee;
      }

      .content-checkbox {
        width: 19px;
        margin-top: 1px;
        margin-right: 7px;
      }
    }
  `}
`
